import React, { useState, useEffect } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { FileDropZone } from '@/components/FileDropZone';
import { SubscriptionCard } from '@/components/SubscriptionCard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { formatBRL, maskBRL, parseBRL, maskPhone, maskCpfCnpj } from '@/lib/formatters';
import { validateCPFCNPJ } from '../utils/validation';
import { useCnpjLookup } from '../hooks/useCnpjLookup';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Loader2, FileText, Paperclip, Upload, Truck, AlertCircle, Eye, Pencil, Trash2 } from 'lucide-react';

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
  'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
  'SP', 'SE', 'TO'
];

const EMPTY_FORM = {
  unidade: '',
  contador_id: '',
  fornecedor_id: '',
  fornecedor_nome: '',
  data_nf: '',
  valor: '',
  mes_referencia: '',
  forma_pagamento: 'PIX',
};

const MESES = [
  { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' }, { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' }, { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' },
];
const ANO_ATUAL = new Date().getFullYear();
const ANOS = Array.from({ length: 4 }, (_, i) => String(ANO_ATUAL - 3 + i));

const NotasFiscais = ({ user, onLogout }) => {
  const company = JSON.parse(localStorage.getItem('company') || '{}');

  const [notas, setNotas] = useState([]);
  const [contadores, setContadores] = useState([]);
  const [fornecedores, setFornecedores] = useState([]);
  const [editingNota, setEditingNota] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [filtroUnidade, setFiltroUnidade] = useState('all');
  const [filtroContador, setFiltroContador] = useState('all');
  const [filtroMesSelect, setFiltroMesSelect] = useState('');
  const [filtroAnoSelect, setFiltroAnoSelect] = useState('');
  const filtroMes = filtroMesSelect && filtroAnoSelect ? `${filtroAnoSelect}-${filtroMesSelect}` : '';

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [arquivoPdf, setArquivoPdf] = useState(null);
  const [arquivoXml, setArquivoXml] = useState(null);

  const [showArquivoModal, setShowArquivoModal] = useState(false);
  const [notaArquivo, setNotaArquivo] = useState(null);
  const [novoPdf, setNovoPdf] = useState(null);
  const [novoXml, setNovoXml] = useState(null);
  const [salvandoArquivo, setSalvandoArquivo] = useState(false);

  const [showNovoFornecedorDialog, setShowNovoFornecedorDialog] = useState(false);
  const [fornecedorFormData, setFornecedorFormData] = useState({
    nome: '', cnpj_cpf: '', telefone: '', whatsapp: '', email: '',
    endereco: '', cidade: '', uf: '', fornecedor_de: '', site: '', observacoes: ''
  });
  const [cnpjError, setCnpjError] = useState('');
  const [savingFornecedor, setSavingFornecedor] = useState(false);
  const { buscarCNPJ, loadingCNPJ } = useCnpjLookup();

  useEffect(() => {
    if (company?.id) {
      fetchContadores();
      fetchFornecedores();
    }
  }, [company?.id]);

  useEffect(() => {
    if (company?.id) {
      fetchNotas();
    }
  }, [company?.id, filtroUnidade, filtroContador, filtroMes]);

  const fetchContadores = async () => {
    try {
      const response = await axiosInstance.get(`/contadores/${company.id}`, { params: { ativo: true } });
      setContadores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contadores');
    }
  };

  const fetchFornecedores = async () => {
    try {
      const response = await axiosInstance.get(`/fornecedores/${company.id}?status=Ativo`);
      setFornecedores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar fornecedores');
    }
  };

  const handleFornecedorCnpjChange = async (value) => {
    const formatted = maskCpfCnpj(value);
    setFornecedorFormData(prev => ({ ...prev, cnpj_cpf: formatted }));
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11 || digits.length === 14) {
      if (!validateCPFCNPJ(formatted)) {
        setCnpjError(digits.length === 11 ? 'CPF inválido' : 'CNPJ inválido');
      } else {
        setCnpjError('');
        if (digits.length === 14) {
          const dados = await buscarCNPJ(digits);
          if (dados) {
            setFornecedorFormData(prev => ({
              ...prev,
              nome: dados.razao_social || prev.nome,
              email: dados.email || prev.email,
              telefone: dados.telefone || prev.telefone,
              cidade: dados.municipio || prev.cidade,
              uf: dados.uf || prev.uf,
              endereco: dados.logradouro ? [dados.logradouro, dados.numero].filter(Boolean).join(', ') : prev.endereco,
            }));
          }
        }
      }
    } else {
      setCnpjError('');
    }
  };

  const handleAbrirNovoFornecedor = () => {
    setCnpjError('');
    setFornecedorFormData({ nome: '', cnpj_cpf: '', telefone: '', whatsapp: '', email: '', endereco: '', cidade: '', uf: '', fornecedor_de: '', site: '', observacoes: '' });
    setShowNovoFornecedorDialog(true);
  };

  const handleSalvarNovoFornecedor = async (e) => {
    e.preventDefault();
    if (!fornecedorFormData.nome.trim()) {
      toast.error('Nome/Razão Social é obrigatório');
      return;
    }
    if (fornecedorFormData.cnpj_cpf) {
      const digits = fornecedorFormData.cnpj_cpf.replace(/\D/g, '');
      if ((digits.length === 11 || digits.length === 14) && !validateCPFCNPJ(fornecedorFormData.cnpj_cpf)) {
        toast.error(digits.length === 11 ? 'CPF inválido' : 'CNPJ inválido');
        return;
      }
    }
    try {
      setSavingFornecedor(true);
      const response = await axiosInstance.post('/fornecedores', { empresa_id: company.id, ...fornecedorFormData });
      const novoFornecedor = { id: response.data.id, nome: fornecedorFormData.nome, empresa_id: company.id, ...fornecedorFormData };
      setFornecedores(prev => [...prev, novoFornecedor]);
      setFormData(prev => ({ ...prev, fornecedor_id: novoFornecedor.id, fornecedor_nome: novoFornecedor.nome }));
      setShowNovoFornecedorDialog(false);
      toast.success('Fornecedor cadastrado!');
    } catch (error) {
      toast.error('Erro ao salvar fornecedor');
    } finally {
      setSavingFornecedor(false);
    }
  };

  const fetchNotas = async () => {
    try {
      setLoading(true);
      const params = { empresa_id: company.id };
      if (filtroUnidade !== 'all') params.unidade = filtroUnidade;
      if (filtroContador !== 'all') params.contador_id = filtroContador;
      if (filtroMes) params.mes = filtroMes;
      const response = await axiosInstance.get('/notas-fiscais', { params });
      setNotas(response.data);
    } catch (error) {
      toast.error('Erro ao carregar notas fiscais');
    } finally {
      setLoading(false);
    }
  };

  const contadoresDaUnidade = formData.unidade
    ? contadores.filter((c) => c.empresas_atendidas.includes(formData.unidade))
    : [];

  const handleOpenModal = (nota = null) => {
    setEditingNota(nota);
    setFormData(nota ? {
      unidade: nota.unidade,
      contador_id: nota.contador_id,
      fornecedor_id: nota.fornecedor_id,
      fornecedor_nome: nota.fornecedor_nome,
      data_nf: nota.data_nf,
      valor: maskBRL(String(Math.round((nota.valor || 0) * 100))),
      mes_referencia: nota.mes_referencia,
      forma_pagamento: nota.forma_pagamento,
    } : EMPTY_FORM);
    setArquivoPdf(null);
    setArquivoXml(null);
    setShowModal(true);
  };

  const handleUnidadeChange = (unidade) => {
    setFormData({ ...formData, unidade, contador_id: '' });
  };

  const handleDataNfChange = (data_nf) => {
    setFormData({ ...formData, data_nf, mes_referencia: data_nf ? data_nf.slice(0, 7) : '' });
  };

  const handleFornecedorChange = (fornecedor_id) => {
    const forn = fornecedores.find((f) => f.id === fornecedor_id);
    setFormData({ ...formData, fornecedor_id, fornecedor_nome: forn?.nome || '' });
  };

  const handleFormaPagamentoChange = (forma_pagamento) => {
    setFormData({ ...formData, forma_pagamento });
  };

  const handleFileChange = (e, tipo) => {
    const file = e.target.files[0];
    if (!file) return;
    const extensao = tipo === 'pdf' ? '.pdf' : '.xml';
    if (!file.name.toLowerCase().endsWith(extensao)) {
      toast.error(`Selecione um arquivo ${extensao}`);
      e.target.value = '';
      return;
    }
    if (tipo === 'pdf') setArquivoPdf(file);
    else setArquivoXml(file);
  };

  const handleOpenArquivoModal = (nota) => {
    setNotaArquivo(nota);
    setNovoPdf(null);
    setNovoXml(null);
    setShowArquivoModal(true);
  };

  const handleViewDocumento = async (nota) => {
    try {
      const fileRes = await axiosInstance.get(`/notas-fiscais/${nota.id}/arquivo`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(fileRes.data);
      window.open(url, '_blank');
    } catch {
      toast.error('Erro ao abrir documento');
    }
  };

  const handleNovoArquivoChange = (e, tipo) => {
    const file = e.target.files[0];
    if (!file) return;
    const extensao = tipo === 'pdf' ? '.pdf' : '.xml';
    if (!file.name.toLowerCase().endsWith(extensao)) {
      toast.error(`Selecione um arquivo ${extensao}`);
      e.target.value = '';
      return;
    }
    if (tipo === 'pdf') setNovoPdf(file);
    else setNovoXml(file);
  };

  const handleSalvarArquivo = async () => {
    if (!novoPdf && !novoXml) {
      toast.error('Selecione ao menos um arquivo (PDF ou XML)');
      return;
    }
    try {
      setSalvandoArquivo(true);
      const fd = new FormData();
      if (novoPdf) fd.append('arquivo_pdf', novoPdf);
      if (novoXml) fd.append('arquivo_xml', novoXml);

      await axiosInstance.patch(`/notas-fiscais/${notaArquivo.id}/arquivo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Arquivo atualizado com sucesso!');
      setShowArquivoModal(false);
      fetchNotas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar arquivo');
    } finally {
      setSalvandoArquivo(false);
    }
  };

  const handleDelete = async (nota) => {
    if (!window.confirm(`Excluir a nota de "${nota.fornecedor_nome}"?`)) return;
    try {
      await axiosInstance.delete(`/notas-fiscais/${nota.id}`);
      toast.success('Nota fiscal excluída com sucesso!');
      fetchNotas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir nota fiscal');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.unidade || !formData.contador_id || !formData.fornecedor_id || !formData.data_nf || !formData.valor) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (!editingNota && !arquivoPdf) {
      toast.error('O arquivo PDF (DANFE) é obrigatório');
      return;
    }

    try {
      setSaving(true);
      if (editingNota) {
        await axiosInstance.put(`/notas-fiscais/${editingNota.id}`, {
          unidade: formData.unidade,
          contador_id: formData.contador_id,
          fornecedor_id: formData.fornecedor_id,
          fornecedor_nome: formData.fornecedor_nome,
          data_nf: formData.data_nf,
          valor: parseBRL(formData.valor),
          forma_pagamento: formData.forma_pagamento,
          mes_referencia: formData.mes_referencia || formData.data_nf.slice(0, 7),
        });
        toast.success('Nota fiscal atualizada com sucesso!');
        setShowModal(false);
        fetchNotas();
        return;
      }
      const fd = new FormData();
      fd.append('empresa_id', company.id);
      fd.append('unidade', formData.unidade);
      fd.append('contador_id', formData.contador_id);
      fd.append('fornecedor_id', formData.fornecedor_id);
      fd.append('fornecedor_nome', formData.fornecedor_nome);
      fd.append('data_nf', formData.data_nf);
      fd.append('valor', parseBRL(formData.valor));
      fd.append('forma_pagamento', formData.forma_pagamento);
      if (formData.mes_referencia) fd.append('mes_referencia', formData.mes_referencia);
      fd.append('arquivo_pdf', arquivoPdf);
      if (arquivoXml) fd.append('arquivo_xml', arquivoXml);

      await axiosInstance.post('/notas-fiscais', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Nota fiscal lançada com sucesso!');
      setShowModal(false);
      fetchNotas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao lançar nota fiscal');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950 text-white">
      <Sidebar user={user} onLogout={onLogout} activePage="notas-fiscais" />

      <div className="flex-1 p-8">
        <div className="space-y-6">
          <SubscriptionCard user={user} />

          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <FileText className="w-8 h-8 text-orange-400" />
                Notas de Compra
              </h1>
              <p className="text-zinc-400 mt-1">Lançamento de Notas Fiscais de Compra</p>
            </div>
            <Button onClick={() => handleOpenModal()} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" /> Nova Nota
            </Button>
          </div>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <Select value={filtroUnidade} onValueChange={setFiltroUnidade}>
                  <SelectTrigger className="w-[160px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as Empresas</SelectItem>
                    <SelectItem value="matriz">Matriz</SelectItem>
                    <SelectItem value="filial">Filial</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filtroContador} onValueChange={setFiltroContador}>
                  <SelectTrigger className="w-[200px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Contador" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os Contadores</SelectItem>
                    {contadores.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroMesSelect} onValueChange={setFiltroMesSelect}>
                  <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filtroAnoSelect} onValueChange={setFiltroAnoSelect}>
                  <SelectTrigger className="w-[110px] bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Ano" />
                  </SelectTrigger>
                  <SelectContent>
                    {ANOS.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Notas Fiscais Lançadas</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
                </div>
              ) : notas.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma nota fiscal lançada</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800">
                        <TableHead>Empresa</TableHead>
                        <TableHead>Fornecedor</TableHead>
                        <TableHead>Data NF</TableHead>
                        <TableHead>Valor</TableHead>
                        <TableHead>Pagamento</TableHead>
                        <TableHead>Mês Ref.</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {notas.map((nota) => (
                        <TableRow key={nota.id} className="border-zinc-800 hover:bg-zinc-800/50">
                          <TableCell className="capitalize">{nota.unidade}</TableCell>
                          <TableCell>{nota.fornecedor_nome}</TableCell>
                          <TableCell>{nota.data_nf}</TableCell>
                          <TableCell>{formatBRL(nota.valor)}</TableCell>
                          <TableCell>{nota.forma_pagamento}</TableCell>
                          <TableCell>{nota.mes_referencia}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={nota.status === 'enviada'
                                ? 'border-green-500/50 text-green-400'
                                : 'border-amber-500/50 text-amber-400'
                              }
                            >
                              {nota.status === 'enviada' ? 'Enviada' : 'Pendente'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleViewDocumento(nota)}
                              title="Visualizar documento"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenArquivoModal(nota)}
                              title="Substituir PDF/XML"
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenModal(nota)}
                              title="Editar"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(nota)}
                              title="Excluir"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-400" />
              {editingNota ? 'Editar Nota Fiscal' : 'Nova Nota Fiscal'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Empresa *</Label>
              <Select value={formData.unidade} onValueChange={handleUnidadeChange}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="matriz">Matriz</SelectItem>
                  <SelectItem value="filial">Filial</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Contador *</Label>
              <Select
                value={formData.contador_id}
                onValueChange={(v) => setFormData({ ...formData, contador_id: v })}
                disabled={!formData.unidade}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder={formData.unidade ? 'Selecione' : 'Selecione a empresa primeiro'} />
                </SelectTrigger>
                <SelectContent>
                  {contadoresDaUnidade.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Fornecedor *</Label>
              <div className="flex gap-2">
                <Select value={formData.fornecedor_id} onValueChange={handleFornecedorChange}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 flex-1">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {fornecedores.map((f) => (
                      <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleAbrirNovoFornecedor}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-700 shrink-0"
                  title="Cadastrar novo fornecedor"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="data_nf">Data da NF/DANFE *</Label>
                <Input
                  id="data_nf"
                  type="date"
                  value={formData.data_nf}
                  onChange={(e) => handleDataNfChange(e.target.value)}
                  className="bg-zinc-800 border-zinc-700"
                  required
                />
              </div>
              <div>
                <Label htmlFor="valor">Valor *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">R$</span>
                  <Input
                    id="valor"
                    type="text"
                    inputMode="numeric"
                    value={formData.valor}
                    onChange={(e) => setFormData({ ...formData, valor: maskBRL(e.target.value) })}
                    className="bg-zinc-800 border-zinc-700 pl-10"
                    placeholder="0,00"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="mes_referencia">Mês de Referência</Label>
              <Input
                id="mes_referencia"
                type="month"
                value={formData.mes_referencia}
                onChange={(e) => setFormData({ ...formData, mes_referencia: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
              <p className="text-xs text-zinc-500 mt-1">Calculado automaticamente pela Data da NF — pode ser alterado para lançamentos em atraso</p>
            </div>

            <div>
              <Label>Forma de Pagamento *</Label>
              <Select value={formData.forma_pagamento} onValueChange={handleFormaPagamentoChange}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PIX">PIX</SelectItem>
                  <SelectItem value="Boleto">Boleto</SelectItem>
                  <SelectItem value="Cartão">Cartão</SelectItem>
                  <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="Transferência">Transferência</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!editingNota && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="arquivo_pdf">DANFE (PDF) *</Label>
                <FileDropZone onDropFiles={(files) => handleFileChange({ target: { files } }, 'pdf')}>
                  <Input
                    id="arquivo_pdf"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleFileChange(e, 'pdf')}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  {arquivoPdf && <p className="text-xs text-green-400 mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" />{arquivoPdf.name}</p>}
                </FileDropZone>
              </div>
              <div>
                <Label htmlFor="arquivo_xml">XML da NF</Label>
                <FileDropZone onDropFiles={(files) => handleFileChange({ target: { files } }, 'xml')}>
                  <Input
                    id="arquivo_xml"
                    type="file"
                    accept=".xml"
                    onChange={(e) => handleFileChange(e, 'xml')}
                    className="bg-zinc-800 border-zinc-700"
                  />
                  {arquivoXml && <p className="text-xs text-green-400 mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" />{arquivoXml.name}</p>}
                </FileDropZone>
              </div>
            </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingNota ? 'Salvar' : 'Lançar Nota'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showArquivoModal} onOpenChange={setShowArquivoModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-orange-400" />
              Substituir Arquivo da Nota
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-zinc-400">{notaArquivo?.fornecedor_nome}</p>

            <div>
              <Label htmlFor="novo_pdf">DANFE (PDF)</Label>
              <Input
                id="novo_pdf"
                type="file"
                accept=".pdf"
                onChange={(e) => handleNovoArquivoChange(e, 'pdf')}
                className="bg-zinc-800 border-zinc-700"
              />
              {novoPdf && <p className="text-xs text-green-400 mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" />{novoPdf.name}</p>}
            </div>

            <div>
              <Label htmlFor="novo_xml">XML da NF</Label>
              <Input
                id="novo_xml"
                type="file"
                accept=".xml"
                onChange={(e) => handleNovoArquivoChange(e, 'xml')}
                className="bg-zinc-800 border-zinc-700"
              />
              {novoXml && <p className="text-xs text-green-400 mt-1 flex items-center gap-1"><Paperclip className="w-3 h-3" />{novoXml.name}</p>}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setShowArquivoModal(false)} className="border-zinc-700">
              Cancelar
            </Button>
            <Button onClick={handleSalvarArquivo} disabled={salvandoArquivo} className="bg-orange-600 hover:bg-orange-700">
              {salvandoArquivo ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNovoFornecedorDialog} onOpenChange={setShowNovoFornecedorDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-400" />
              Novo Fornecedor
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSalvarNovoFornecedor} className="space-y-4">
            <div>
              <Label htmlFor="forn_nome">Nome/Razão Social *</Label>
              <Input
                id="forn_nome"
                value={fornecedorFormData.nome}
                onChange={(e) => setFornecedorFormData(prev => ({ ...prev, nome: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Nome do fornecedor"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_cnpj" className="flex items-center gap-2">
                  CNPJ/CPF
                  {loadingCNPJ && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
                </Label>
                <Input
                  id="forn_cnpj"
                  value={fornecedorFormData.cnpj_cpf}
                  onChange={(e) => handleFornecedorCnpjChange(e.target.value)}
                  className={`bg-zinc-800 border-zinc-700 ${cnpjError ? 'border-red-500' : ''}`}
                  placeholder="00.000.000/0001-00"
                />
                {cnpjError && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {cnpjError}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="forn_fornecedor_de">Fornecedor de</Label>
                <Input
                  id="forn_fornecedor_de"
                  value={fornecedorFormData.fornecedor_de}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, fornecedor_de: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Ex: Materiais elétricos, Mão de obra"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_telefone">Telefone</Label>
                <Input
                  id="forn_telefone"
                  value={fornecedorFormData.telefone}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, telefone: maskPhone(e.target.value) }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="(00) 0000-0000"
                />
              </div>
              <div>
                <Label htmlFor="forn_whatsapp">WhatsApp</Label>
                <Input
                  id="forn_whatsapp"
                  value={fornecedorFormData.whatsapp}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, whatsapp: maskPhone(e.target.value) }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="(00) 00000-0000"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_email">Email</Label>
                <Input
                  id="forn_email"
                  type="email"
                  value={fornecedorFormData.email}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="email@fornecedor.com"
                />
              </div>
              <div>
                <Label htmlFor="forn_site">Site</Label>
                <Input
                  id="forn_site"
                  value={fornecedorFormData.site}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, site: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="www.fornecedor.com.br"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="forn_endereco">Endereço</Label>
              <Input
                id="forn_endereco"
                value={fornecedorFormData.endereco}
                onChange={(e) => setFornecedorFormData(prev => ({ ...prev, endereco: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Rua, número, bairro"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="forn_cidade">Cidade</Label>
                <Input
                  id="forn_cidade"
                  value={fornecedorFormData.cidade}
                  onChange={(e) => setFornecedorFormData(prev => ({ ...prev, cidade: e.target.value }))}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Cidade"
                />
              </div>
              <div>
                <Label htmlFor="forn_uf">UF</Label>
                <Select value={fornecedorFormData.uf} onValueChange={(value) => setFornecedorFormData(prev => ({ ...prev, uf: value }))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {ESTADOS_BR.map((uf) => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="forn_observacoes">Observações</Label>
              <Textarea
                id="forn_observacoes"
                value={fornecedorFormData.observacoes}
                onChange={(e) => setFornecedorFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                className="bg-zinc-800 border-zinc-700"
                placeholder="Observações sobre o fornecedor..."
                rows={3}
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setShowNovoFornecedorDialog(false)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button type="submit" disabled={savingFornecedor} className="bg-orange-600 hover:bg-orange-700">
                {savingFornecedor ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Cadastrar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotasFiscais;
